import { FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom"

import { Button, Dropdown, MenuProps, Skeleton, Typography } from "antd";
import { DownOutlined, MailOutlined, MoreOutlined } from "@ant-design/icons";

import { Tabs } from "@hdi-seguros/web-components.tabs";
import { Tags } from "@hdi-seguros/web-components.card";

import InsurancePolicyService from "../../../shared/services/insurancePolicy";
import { ErrorResults, RowSize, ColSize } from "../../../shared/components";
import { Claim, DECISION_TYPE, Endorsement, InsurancePolicy, BINARY_VALUES, PolicyStates, Possessions, PossessionsServiceResponse, PossessionsType, PreviousPDF, PreviousPDFSRequest, PaymentMethod, Ubication } from "../../../shared/interfaces";
import { Branches } from "../../../shared/interfaces/services/branches-enum";
import { PageHeader, PageHeaderActions } from "../../../shared/components/MainLayout/components";
import { InfoContext, UIContext } from "../../../shared/contexts";
import PossessionsService, { PossessionsServiceParams } from "../../../shared/services/posessions";
import ProducersService from "../../../shared/services/producers";
import { encrypt, getTagsByStates } from "../../../shared/utils";

import {
    ClaimsTab,
    DocumentsTab,
    MainTab,
    PossessionsTab,
    PreviousPDFTab,
    QuotesTab,
    InsuranceTab,
    SuscribePolicyModal,
    PayrollTab,
} from './components';

import './index.css';
import { RevertEndorsementModal } from "./components/RevertEndorsementModal";
import { PaymentFormatModal } from "./components/PaymenFormatModal";
import { QuotationGroups, QuotationInfoProvider } from "../../quotation/NewQuote/containers";
import { RenewalQuotesContext } from "../../quotation/SavedQuotes/shared/contexts/RenewalQuotes";
import { TourComponent } from "../../../shared/components/Tour";
import { TotalPolicyBalance } from "../../../shared/interfaces/services/get-total-policy-balance-service";
import { ARCA_STATUS } from "../../../shared/interfaces/enums/arca-status-enum";

interface Tab {
    label: string;
    children: JSX.Element,
    key: string,
    closable: boolean;
}
interface InsurancePolicyDetailProps {
    title: string;
}

export const InsurancePolicyDetail: FC<InsurancePolicyDetailProps> = () => {

    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [ queryParams ] = useSearchParams();

    const policyFromList = (location.state as {policyFromList?:InsurancePolicy} | null);

    const branch = queryParams.get('branch');

    const article = queryParams.get('article');

    const superpolicy = queryParams.get('superpolicy');

    const { isLoading, showError, setIsLoading, setShowError } = useContext( UIContext );
    const { setTitle } = useContext(InfoContext);

    const { 
        getQuotationGroupByArticle,
    } = useContext(RenewalQuotesContext);

    const [ activeTab, setActiveTab ] = useState('1');
    const [ showSuscriptionModal, setShowSuscriptionModal ] = useState<boolean>(false);
    const [ showReverseEndorsementModal, setShowReverseEndorsementModal ] = useState<boolean>(false);
    const [ showPaymentFormatModal, setShowPaymentFormatModal ] = useState<boolean>(false);

    const [ insurancePolicyDetail, setInsurancePolicyDetail ] = useState<InsurancePolicy | undefined>(undefined);
    const [ endorsementsPerPolicy, setEndorsementsPerPolicy ] = useState<Array<Endorsement>>([]);
    const [ totalPolicyBalance, setTotalPolicyBalance ] = useState<TotalPolicyBalance | undefined>(undefined);
    const [ selectedEndorsement, setSelectedEndorsement ] = useState<Endorsement | undefined>(undefined);
    const [ claimsPerPolicy, setClaimsPerPolicy ] = useState<Array<Claim>>([]);
    const [ possessionsList, setPossessionsList ] = useState<Array<Possessions>>();
    const [ previousPDFS, setpreviousPDFS ] = useState<Array<PreviousPDF>>([]);
    const [insuredsByPolicy, setInsuredsByPolicy] = useState<any | undefined>();

    const [showTour, setShowTour] = useState<boolean>(false);
    const [ showPossessionsTabError, setShowPossessionsTabError ] = useState<boolean>(false);
    const [ showMainTabError, setShowMainTabError ] = useState<boolean>(false);
    const [ showInsuranceTabError, setShowInsuranceTabError ] = useState<boolean>(false);
    const [ showClaimTabError, setShowClaimTabError ] = useState<boolean>(false);
    const [ showPreviousPDFTabError, setShowPreviousPDFTabError ] = useState<boolean>(false);

    const getPossessionsList = async () => {
        const params: PossessionsServiceParams = {
            rama: insurancePolicyDetail!.rama,
            poliza: id!,
            articulo: insurancePolicyDetail!.articulo,
            superpoliza: insurancePolicyDetail!.superpoliza,
        }

        let response: PossessionsServiceResponse;

        switch ( insurancePolicyDetail?.possessionsType ) {
            case PossessionsType.BIENES_AUTOS:
                response = await PossessionsService.getCarPossessions( params );
                break;
            case PossessionsType.BIENES_EMBARCACIONES:
                response = await PossessionsService.getVesselsPossessions( params );
                break;
            case PossessionsType.BIENES_RIESGOS_VARIOS:
                response = await PossessionsService.getVariousRiskPossessions( params );
                break;
            case PossessionsType.BIENES_VIDA:
                response = await PossessionsService.getLifePossessions( params );
                break;
            default:
                break;
        }

        return response!;
    };

    const getPossessionsByPolicy = async () => {
        try {
            setIsLoading(true);
            const response = await getPossessionsList();
            if( response?.possessions ) {
                setPossessionsList( response.possessions );
                setShowError(false);
                setShowPossessionsTabError(false);
            }
        } catch (error) {
            setShowPossessionsTabError( true );
        } finally {
            setIsLoading(false)
        }
    }

    const getInsurancePolicyDetailById = async () => {
        try {
            setIsLoading(true);
            const response = await InsurancePolicyService.getInsurancePolicyById( id!, branch! );
            if( response.data.polizas?.length ) {
                const mergedPolicy : InsurancePolicy = {
                    ...response.data.polizas[0],
                    tipoPersona: policyFromList?.policyFromList?.tipoPersona || ""
                }
                setInsurancePolicyDetail( mergedPolicy );
                setShowError(false);
            } else {
                setShowError( true );
            }
        } catch (error) {
            setShowError(true);
        } finally {
            setIsLoading(false)
        }
    }

    const getPolicyEndorsements = async () => {
        try {
            setIsLoading(true);
            const response = await InsurancePolicyService.getPolicyEndorsements( id!, branch! );
            if( response.data.suplementos?.length ) {
                setEndorsementsPerPolicy( response.data.suplementos );
                setShowMainTabError( false );
            }
        } catch (error) {
            setShowMainTabError( true );
        } finally {
            setIsLoading(false)
        }
    }

     const getTotalPolicyBalance = async () => {
        try {
            setIsLoading(true);
            const response = await InsurancePolicyService.getTotalPolicyBalance( id!, branch!,article!,superpolicy! );
            if( response.data ) {
                setTotalPolicyBalance( response.data );
                setShowMainTabError( false );
            }
        } catch (error) {
            setShowMainTabError( true );
        } finally {
            setIsLoading(false)
        }
    }

    const getPreviousPDFS = async () => {
        const params: PreviousPDFSRequest = {
            rama: insurancePolicyDetail!.rama,
            poliza: id!,
            articulo: insurancePolicyDetail!.articulo,
            superpoliza: insurancePolicyDetail!.superpoliza,
            articuloRamaSecuencia: insurancePolicyDetail!.arse,
            operacion: insurancePolicyDetail!.operacion
        }

        try {
            setIsLoading(true);
            const response = await InsurancePolicyService.getPreviousPDFS( params );
            if( response.data?.polizasAnteriores ) {
                setpreviousPDFS( response.data.polizasAnteriores );
                setShowError(false);
                setShowPreviousPDFTabError(false);
            }
        } catch (error) {
            setShowError(true);
            setShowPreviousPDFTabError(true);
        } finally {
            setIsLoading(false)
        }
    }

    const getClaimsPerPolicy = async () => {
        try {
            setIsLoading(true);
            const response = await InsurancePolicyService.getClaimsPerPolicy( id!, branch! );
            if( response.data?.siniestros ) {
                setClaimsPerPolicy( response.data.siniestros );
                setShowError(false);
                setShowClaimTabError(false);
            }
        } catch (error) {
            setShowError(true);
            setShowClaimTabError(true);
        } finally {
            setIsLoading(false)
        }
    };

    const getInsuredsByPolicy = async () => {
        try {
            setIsLoading(true);
            const response = await ProducersService.getInsureds( branch!, id! );
            if( response ) {
                setInsuredsByPolicy( response );
                setShowError(false);
                setShowInsuranceTabError(false);
            }
        } catch (error) {
            setShowInsuranceTabError( true );
        } finally {
            setIsLoading(false)
        }
    }

    const updatePolicySuscriptionInfo = (suscription: string, email: string) => setInsurancePolicyDetail((prevState: any) => ({
        ...prevState,
        suscriptaPolizaElectronica: suscription,
        mailPolizaElectronica: email,
    }))

    const updatePaymentFormat = (paymentFormat: PaymentMethod) =>{
        setInsurancePolicyDetail((prevState: any) => ({
            ...prevState, 
            formaDePago: paymentFormat
        }))
    } 

    const handleUpdateEndorsement = () => {
        getPolicyEndorsements();
    };

    useEffect(() => {
        if (endorsementsPerPolicy.length > 0) {
            setSelectedEndorsement(endorsementsPerPolicy[0]);
        }
    }, [endorsementsPerPolicy]);
    

    const handlePressTab = (activeTab: string) => setActiveTab(activeTab);
    const TABS: Array<Tab> = useMemo(() => [
        {
            label: 'Principal',
            children: <MainTab insurancePolicyDetail={ insurancePolicyDetail } selectedEndorsement={ selectedEndorsement } showError={ showMainTabError } />,
            key: '1',
            closable: false,
        },
        {
            label: 'Asegurado',
            children: <InsuranceTab insurancePolicyDetail={ insurancePolicyDetail } insuredsByPolicy={ insuredsByPolicy } showError={ showInsuranceTabError } /> ,
            key: '2',
            closable: false,
        },
        {
            label: 'Bienes',
            children: <PossessionsTab insurancePolicyDetail={ insurancePolicyDetail! } possessionsList={ possessionsList } showError={ showPossessionsTabError } endorsement={ selectedEndorsement } insuredsByPolicy={ insuredsByPolicy } />,
            key: '3',
            closable: false,
        },
        {
            label: 'Cuotas',
            children: <QuotesTab insurancePolicyDetail={ insurancePolicyDetail } id={ id } endorsement={selectedEndorsement} insuredsByPolicy={insuredsByPolicy} onUpdateEndorsement={handleUpdateEndorsement} totalPolicyBalance={totalPolicyBalance} />,
            key: '4',
            closable: false,
        },
        {
            label: 'Siniestros',
            children: <ClaimsTab claimsPerPolicy={ claimsPerPolicy } showError={ showClaimTabError } />,
            key: '5',
            closable: false,
        },
        {
            label: 'Documentos',
            children: <DocumentsTab insurancePolicyDetail={ insurancePolicyDetail } insuredsByPolicy={insuredsByPolicy} possessionsList={possessionsList} endorsement={selectedEndorsement}  id={ id } />,
            key: '6',
            closable: false,
        },
        ...( insurancePolicyDetail?.rama === "23"
            ? [{
                label: 'A y B Nómina',
                children: <PayrollTab insurancePolicyDetail={insurancePolicyDetail} insuredsByPolicy={insuredsByPolicy} possessionsList={possessionsList} />,
                key: '7',
                closable: false,
              }]
            : []
          ),
        {
            label: 'PDF Anteriores',
            children: <PreviousPDFTab previousPDFS={ previousPDFS } showError={ showPreviousPDFTabError } />,
            key: '8',
            closable: false,
        },
    ], [
        id, 
        insurancePolicyDetail, 
        selectedEndorsement, 
        possessionsList, 
        previousPDFS,
        claimsPerPolicy,
        insuredsByPolicy,
        showClaimTabError,
        showInsuranceTabError,
        showMainTabError,
        showPossessionsTabError,
        showPreviousPDFTabError,
        totalPolicyBalance,
    ]);

    

    const selectEndorsement = (key: string) => {
        const selectedEndorsement = endorsementsPerPolicy.find((el) => el.nro === key);
        if( selectedEndorsement ) {
            setSelectedEndorsement( selectedEndorsement );
        }
    }

    const DROPDOWN_ENDORSEMENTS_OPTIONS: MenuProps['items'] = useMemo(() => endorsementsPerPolicy?.map(( e ) => ({
        label: `Endoso ${e.nro}`,
        key: e.nro,
    })), [endorsementsPerPolicy]);

    const openSuscriptionModal =useCallback( () => setShowSuscriptionModal(true), [setShowSuscriptionModal]);

    const onPressRenewPolicy = () => renewPolicy();

    const renewPolicy = () => {            
        onRenewQuote(insurancePolicyDetail!.nroPoliza, insurancePolicyDetail!.articulo, insurancePolicyDetail!.superpoliza);
    };

    const onRenewQuote = useCallback((quotationNumber: string, article: string, superQuotationNumber: string) => {                       
        const groupSelected = getQuotationGroupByArticle(article);
        
        const quotationInfo = { quotationNumber, groupSelected, article, superQuotationNumber };
        const parseQuoteInfo = encrypt<{ quotationNumber: string, groupSelected: QuotationGroups, article: string, superQuotationNumber : string }>(quotationInfo);
        navigate(`/cotizador/renovar-cotizacion/${parseQuoteInfo}`);
    }, [navigate, getQuotationGroupByArticle]);

    const openRevertEndorsement =useCallback( () => setShowReverseEndorsementModal(true), [setShowReverseEndorsementModal]);
    const openPaymentFormatModal =useCallback( () => setShowPaymentFormatModal(true), [setShowPaymentFormatModal]);


    const ACTIONS = useMemo(() => {
        const actions: Array<PageHeaderActions> = [
            {
                label: insurancePolicyDetail?.suscriptaPolizaElectronica === DECISION_TYPE.YES ? 'Desuscribir' : 'Suscribir',
                icon: <MailOutlined />,
                onClick: () => openSuscriptionModal()
            },
        ];

        const RENEWABLE_BRANCHES = [Branches.AUTOMOVILES, Branches.COMBINADO_FAMILIAR, Branches.INTEGRAL_CONSORCIO];
        const EXCLUDED_AUTOS_ARTICLES = ['1006', '1093'];
        const isRenewableBranch = RENEWABLE_BRANCHES.includes(insurancePolicyDetail?.rama as Branches);
        const isExcludedAutosArticle = insurancePolicyDetail?.rama === Branches.AUTOMOVILES && EXCLUDED_AUTOS_ARTICLES.includes(insurancePolicyDetail?.articulo);
        if(isRenewableBranch && !isExcludedAutosArticle && (insurancePolicyDetail?.estados?.includes(PolicyStates.IS_RENEWAL) && insurancePolicyDetail?.permiteRenovar === DECISION_TYPE.YES)) {
            actions.push({
                label: 'Renovar póliza',
                onClick: onPressRenewPolicy,
            })
        };

        if(insurancePolicyDetail?.tieneEndosoParaRevertir) {
            if(insurancePolicyDetail?.tieneEndosoParaRevertir.includes(DECISION_TYPE.YES)){
              actions.push({
                label: 'Revertir endoso', 
                onClick: openRevertEndorsement
              });
            }
        }
        if(parseInt(insurancePolicyDetail?.saldoPendiente!) > 0) {
            actions.push({
                label: 'Cambiar forma pago',
                onClick: openPaymentFormatModal,
            })
        };

        return actions;
    }, [insurancePolicyDetail, onPressRenewPolicy, openSuscriptionModal, openPaymentFormatModal]);

    const updateRevertEndorsement = () => setInsurancePolicyDetail((prevState: any) => ({
        ...prevState,
        tieneEndosoParaRevertir: DECISION_TYPE.NO
    }))

    const tags = useMemo(() => getTagsByStates(insurancePolicyDetail?.estados), [getTagsByStates, insurancePolicyDetail]);

    useEffect(() => {
        if(endorsementsPerPolicy?.length) {
            setSelectedEndorsement(endorsementsPerPolicy[0]);
        }
        // eslint-disable-next-line
    }, [endorsementsPerPolicy?.length]);

    useEffect(() => {
        setTitle('Detalle de Póliza');
        if(id && branch) {
            getInsurancePolicyDetailById();
        }
     // eslint-disable-next-line
    }, [id, branch]);

    useEffect(() => {
        if( id && insurancePolicyDetail && branch ) {
            getPolicyEndorsements();
            getInsuredsByPolicy();
            getClaimsPerPolicy();
            getPossessionsByPolicy();
            getPreviousPDFS();
            getTotalPolicyBalance();
        }
        // eslint-disable-next-line
    }, [ id, branch, insurancePolicyDetail ])

    const toggleTour = () => {
        setShowTour(!showTour);
    };

    const stepsTour = [
        {
          target: ".custom-dropdown-button",
          content: (
            <p>
              👆 Clickeando aqui podras revisar los diferentes endosos de la póliza.
            </p>
          ),
          disableBeacon: true,
        }
    ];

    return (
        <QuotationInfoProvider>
            <>
                { showError ? <></> : <PageHeader actions={ACTIONS} activeTour={toggleTour} />}
                {
                    isLoading
                        ? <Skeleton active />
                        : showError 
                            ? <ErrorResults title="No se pudo cargar el detalle de la póliza" />
                            :  (
                                <div className="insurance-policy-detail-container">
                                    <TourComponent
                                        showTour={showTour}
                                        steps={stepsTour}
                                        onTourClose={toggleTour}
                                    />
                                    <RowSize
                                        rowProps={{gutter:[0 , 0]}}
                                        className="row-insurance-policy-detail" 
                                    >
                                        <ColSize size={100}>
                                        
                                            <RowSize rowProps={{gutter:[0 , 15]}} className="row-dropdown-insurence-policy-detail">
                                                <Dropdown
                                                    menu={{ items: DROPDOWN_ENDORSEMENTS_OPTIONS, onClick: ({ key }) => selectEndorsement( key ) }}
                                                    trigger={['click']}
                                                    className="custom-dropdown-button"
                                                >
                                                    <Button>
                                                        { id } - { selectedEndorsement?.nro }
                                                        <DownOutlined />
                                                    </Button>
                                                </Dropdown>

                                                <Tags tags={tags} />
                                                
                                                {insurancePolicyDetail?.statusArca === ARCA_STATUS.PENDING_APPROVAL || insurancePolicyDetail?.statusArca === ARCA_STATUS.REJECTED
                                                    ?
                                                    <Tags tags={[{label: 'Pendiente ARCA', color: 'red'}]} /> : ''
                                                }
                                                
                                            </RowSize>
                                        
                                        </ColSize> 
                                    </RowSize >

                                    <RowSize 
                                        rowProps={{gutter:[0 , 0]}}
                                    >   
                                        <ColSize size={100}>  
                                            <Typography.Text strong style={{ fontSize: 16 }}>
                                                { insurancePolicyDetail?.nombreAsegurado.toUpperCase() }
                                            </Typography.Text>
                                        </ColSize>
                                    </RowSize>
        
                                    <RowSize
                                        rowProps={{gutter:[0 , 0]}}
                                        className="row-insurance-policy-detail"
                                    >
                                        <ColSize size={100}>
                                            <Typography.Text style={{ fontSize: 14 }}>
                                                { insurancePolicyDetail?.descripcionRama?.toUpperCase() } / { insurancePolicyDetail?.nombreArticulo }: Superpoliza { insurancePolicyDetail?.superpoliza }
                                            </Typography.Text>
                                        </ColSize>
                                    </RowSize>

                                    {
                                        showSuscriptionModal && (
                                            <SuscribePolicyModal
                                                title={`${ insurancePolicyDetail?.suscriptaPolizaElectronica === 'S' ? 'Desuscripción' : 'Suscripción' } a la póliza electrónica`} 
                                                open={ showSuscriptionModal } 
                                                insurancePolicy={ insurancePolicyDetail }
                                                onClose={() => setShowSuscriptionModal(false)}
                                                onConfirm={ updatePolicySuscriptionInfo }
                                            />
                                        )
                                    }
                                    
                                    {
                                        showReverseEndorsementModal && (
                                            <RevertEndorsementModal
                                                title={'Renovar Endoso'} 
                                                open={ showReverseEndorsementModal } 
                                                insurancePolicy={ insurancePolicyDetail }
                                                onClose={() => setShowReverseEndorsementModal(false)}
                                                onConfirm={ updateRevertEndorsement }
                                            />
                                        )
                                    }

                                    {
                                        showPaymentFormatModal && (
                                            <PaymentFormatModal
                                                title={`Cambiar forma de pago`} 
                                                open={ showPaymentFormatModal } 
                                                insurancePolicy={ insurancePolicyDetail }
                                                onClose={() => setShowPaymentFormatModal(false)}
                                                onConfirm={ updatePaymentFormat }
                                            />
                                        )
                                    }
                                </div>
                            ) 
                }
                {
                    !showError && (
                        <RowSize
                            rowProps={{gutter:[0 , 0]}} 
                        >
                            <ColSize size={100}>
                                <Tabs 
                                    handlePressTab={ handlePressTab }
                                    activeKey={activeTab}
                                    items={ TABS }
                                />
                            </ColSize>
                        </RowSize>
                    )
                }
            </>
        </QuotationInfoProvider>
    )
}
